import React, {useEffect, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {CurrencyType} from 'types/Currency'

import {BottomSheet} from '@vio/atlas/BottomSheet'
import {PopUp} from '@vio/atlas/PopUp'
import {Tab, TabPanel, Tabs} from '@vio/atlas/Tabs'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import minus from '@vio/atlas/utils/minus'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {LanguageType} from '@vio/core/src/localization/types/LanguageType'

import {CurrencySelector} from './CurrencySelector'
import {LanguageSelector} from './LanguageSelector'

export type LanguageCurrencySelectorType = 'language' | 'currency'
interface Props {
  isOpen: boolean
  onClose: () => void
  selectedLanguage?: string
  onChangeLanguage?: (v: string) => void
  selectedCurrency?: string
  onChangeCurrency?: (v: string) => void
  availableLanguages?: LanguageType[]
  availableCurrencies?: CurrencyType[]
  isMobile: boolean
  initialSelector: LanguageCurrencySelectorType
  hasCurrency?: boolean
  onSearchInputFocus?: (type: LanguageCurrencySelectorType) => void
  onChangeActiveTab?: (type: LanguageCurrencySelectorType) => void
  showPriceDisplaySetting?: boolean
  showTotalPrices?: boolean
  onChangePriceDisplay?: (showTotal: boolean) => void
  includeTaxes?: boolean
}

const TabsWrapper = styled.div`
  position: sticky;
  /*
  This padding and negative margin is to avoid elements overflowing the tabs when
  scrolling up.
  */
  padding-top: ${cssTheme.layout.spacing.s600};
  margin: ${minus(cssTheme.layout.spacing.s600)} ${cssTheme.layout.spacing.s200}
    0;
  top: ${minus(cssTheme.layout.spacing.s600)};
  margin-bottom: ${cssTheme.layout.spacing.s600};
  background-color: ${cssTheme.colors.background.neutral.primary};
  z-index: 1;
`

const titleMessages = defineMessages({
  language: {
    id: 'selectLanguage',
    defaultMessage: 'Select language'
  },
  currency: {
    id: 'selectCurrency',
    defaultMessage: 'Select currency'
  }
})

export const LanguageCurrencySelector: React.FC<Props> = ({
  isOpen,
  onClose,
  isMobile,
  availableCurrencies = [],
  availableLanguages = [],
  onChangeCurrency,
  onChangeLanguage,
  selectedCurrency,
  selectedLanguage,
  initialSelector,
  hasCurrency,
  onSearchInputFocus,
  onChangeActiveTab,
  showPriceDisplaySetting,
  showTotalPrices,
  onChangePriceDisplay,
  includeTaxes
}) => {
  const [activeTab, setActiveTab] = useState<string>(initialSelector)
  const {formatMessage} = useIntl()

  useEffect(() => {
    setActiveTab(initialSelector)
  }, [initialSelector])

  useEffect(() => {
    if (!hasCurrency && activeTab === 'currency') {
      setActiveTab('language')
    }
  }, [hasCurrency, activeTab])

  const handleChangeActiveTab = (value: string) => {
    setActiveTab(value)
    onChangeActiveTab?.(value as LanguageCurrencySelectorType)
  }

  const renderSelector = () => {
    if (
      activeTab === 'language' &&
      availableLanguages.length > 0 &&
      selectedLanguage &&
      onChangeLanguage
    ) {
      return (
        <TabPanel value="language" activeTab={activeTab}>
          <LanguageSelector
            isMobile={isMobile}
            languages={availableLanguages}
            onChange={onChangeLanguage}
            value={selectedLanguage}
            onSearchInputFocus={() => onSearchInputFocus?.('language')}
          />
        </TabPanel>
      )
    }

    if (
      hasCurrency &&
      activeTab === 'currency' &&
      onChangeCurrency &&
      selectedCurrency
    )
      return (
        <TabPanel value="currency" activeTab={activeTab}>
          <CurrencySelector
            isMobile={isMobile}
            currencies={availableCurrencies}
            onChange={onChangeCurrency}
            value={selectedCurrency}
            onSearchInputFocus={() => onSearchInputFocus?.('currency')}
            showPriceDisplaySetting={showPriceDisplaySetting}
            showTotalPrices={showTotalPrices}
            onChangePriceDisplay={onChangePriceDisplay}
            includeTaxes={includeTaxes}
          />
        </TabPanel>
      )
  }

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        snapPoints={{top: 'nearFull', middle: 0.95}}
        hasFixedHeight
        navBarProps={{
          centerContent: (
            <Text variant="titleM">
              <FormattedMessageWrapper
                {...titleMessages[activeTab as Props['initialSelector']]}
              />
            </Text>
          )
        }}
        onClose={onClose}
      >
        {renderSelector()}
      </BottomSheet>
    )
  }

  return (
    <PopUp
      isOpen={isOpen}
      width={984}
      height={513}
      navBarProps={{variant: 'transparent', floating: true}}
      closeAction={onClose}
    >
      <TabsWrapper>
        <Tabs activeTab={activeTab} onChange={handleChangeActiveTab}>
          {availableLanguages.length > 0 && (
            <Tab
              value="language"
              label={formatMessage({
                defaultMessage: 'Language',
                id: 'languageLabel'
              })}
            />
          )}
          {hasCurrency && (
            <Tab
              value="currency"
              label={formatMessage({
                defaultMessage: 'Currency',
                id: 'currencyLabel'
              })}
            />
          )}
        </Tabs>
      </TabsWrapper>
      {renderSelector()}
    </PopUp>
  )
}
